import { Button, FormField, Modal, useToast } from '@ntconsult/aluma-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBatchAllocationTags } from '../hooks/useBatchAllocationTags';
import type { Allocation } from '../types/Allocation';
import type { TagOption } from '../types/Tag';
import { i18nKeyForError, parseApiError } from '../utils/errors';
import { MultiSearchCombobox } from './MultiSearchCombobox';
import { TagsCombobox } from './TagsCombobox';

type BatchTagsModalProps = {
  open: boolean;
  onClose: () => void;
  onApplied: () => void;
  allocations: Allocation[];
};

const tagMarker = (tag: TagOption) => (
  <span
    aria-hidden="true"
    className="inline-block h-2 w-2 shrink-0 rounded-full"
    style={{ backgroundColor: tag.color }}
  />
);

function FormBody({ onClose, onApplied, allocations }: Omit<BatchTagsModalProps, 'open'>) {
  const { t } = useTranslation(['alocacao', 'common']);
  const { toast } = useToast();
  const batchMutation = useBatchAllocationTags();
  const [addTags, setAddTags] = useState<TagOption[]>([]);
  const [removeTags, setRemoveTags] = useState<TagOption[]>([]);

  const tagsInUse = useMemo(() => {
    const byId = new Map<number, TagOption>();
    for (const allocation of allocations) {
      for (const tag of allocation.tags) byId.set(tag.id, tag);
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [allocations]);

  const searchTagsInUse = useCallback(
    (text: string) => {
      const needle = text.trim().toLowerCase();
      const items = tagsInUse.filter((tag) => tag.name.toLowerCase().includes(needle));
      return {
        data: { items, total: items.length, page: 1, perPage: Math.max(items.length, 1) },
        isFetching: false,
      };
    },
    [tagsInUse],
  );

  const conflict = addTags.some((tag) => removeTags.some((other) => other.id === tag.id));
  const nothingChosen = addTags.length === 0 && removeTags.length === 0;

  function apply() {
    batchMutation.mutate(
      {
        allocationIds: allocations.map((allocation) => allocation.id),
        addTagIds: addTags.map((tag) => tag.id),
        removeTagIds: removeTags.map((tag) => tag.id),
      },
      {
        onSuccess: (result) => {
          toast.success({ title: t('alocacao:lote.sucesso', { count: result.updated.length }) });
          if (result.skipped.length > 0) {
            toast.warning({
              title: t('alocacao:lote.ignoradas', { count: result.skipped.length }),
            });
          }
          onApplied();
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          toast.danger({
            title: t((apiError ? i18nKeyForError(apiError.code) : 'alocacao:toasts.erro') as never),
          });
        },
      },
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
      noValidate
    >
      <Modal.Body className="flex flex-col gap-4">
        <FormField id="lote-adicionar" label={t('alocacao:lote.adicionar')}>
          <TagsCombobox
            label={t('alocacao:lote.adicionar')}
            value={addTags}
            onChange={setAddTags}
            placeholder={t('alocacao:lote.adicionarPlaceholder')}
            searchPlaceholder={t('alocacao:form.tagsBuscarPlaceholder')}
            emptyLabel={t('alocacao:form.tagsSemResultado')}
            removeChipLabel={(name) => t('alocacao:form.tagsRemover', { nome: name })}
          />
        </FormField>
        <FormField
          id="lote-remover"
          label={t('alocacao:lote.remover')}
          error={conflict ? t('alocacao:lote.conflito') : undefined}
        >
          <MultiSearchCombobox<TagOption, TagOption>
            label={t('alocacao:lote.remover')}
            value={removeTags}
            onChange={setRemoveTags}
            useSearch={searchTagsInUse}
            toOption={(tag) => tag}
            renderMarker={tagMarker}
            placeholder={t('alocacao:lote.removerPlaceholder')}
            searchPlaceholder={t('alocacao:form.tagsBuscarPlaceholder')}
            emptyLabel={t('alocacao:lote.removerSemResultado')}
            removeChipLabel={(name) => t('alocacao:form.tagsRemover', { nome: name })}
          />
        </FormField>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={nothingChosen || conflict}
          loading={batchMutation.isPending}
        >
          {t('alocacao:lote.aplicar')}
        </Button>
      </Modal.Footer>
    </form>
  );
}

export function BatchTagsModal({ open, onClose, ...props }: BatchTagsModalProps) {
  const { t } = useTranslation(['alocacao', 'common']);
  const titleId = useId();

  return (
    <Modal open={open} onClose={onClose} ariaLabelledBy={titleId} size="md">
      <Modal.Header
        titleId={titleId}
        title={t('alocacao:lote.titulo')}
        description={t('alocacao:lote.descricao', { count: props.allocations.length })}
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      {open && <FormBody onClose={onClose} {...props} />}
    </Modal>
  );
}
